import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownRight, ArrowUpRight, BarChart3, ChevronDown, ChevronRight, Download, GitBranch, Minus, Table2, X } from "lucide-react";
import { deltaGoodness } from "@/lib/utils/daylight-insight/compute";
import { periodAxisLabel, periodLabelWithYear, seriesGranularity } from "@/lib/utils/daylight-insight/dates";
import { downloadCsv } from "@/lib/utils/daylight-insight/records";
import { fmtSummaryVal, fmtVal, formatAbsDelta } from "@/lib/utils/daylight-insight/format";
import { SHOW_BENCHMARK_LABEL } from "@/lib/utils/daylight-insight/constants";
import { pillarKey } from "@/lib/utils/daylight-insight/pillars";
import {
  DEFAULT_SPEC,
  filterLabel,
  primaryFilters,
  secondaryFilters,
  summarizeByFor,
  type FilterSpec,
} from "@/lib/utils/daylight-insight/dashboardSpec";
import type { Bucket, Metric } from "@/lib/utils/daylight-insight/types";
import { getMetricSummary } from "@/service/reqUrl.service";
import { TrendChartView } from "@/components/daylight-insight/charts";
import { DrillCompareView } from "./DrillCompareView";
import { apiToMetric, deltaOf } from "@/components/daylight-insight/api/metricsAdapt";
import { useMetricDefinitions } from "@/components/daylight-insight/api/useMetricDefinitions";
import { useMetricRelations } from "@/components/daylight-insight/api/useMetricRelations";
import { useMetricSummary } from "@/components/daylight-insight/api/useMetricSummary";
import type { DashboardMetric, DashboardMetricCategory, MetricDefinition, MetricSummaryRow, RelatedMetricDetail } from "@/components/daylight-insight/api/types";
import { useStore } from "@/provider/store";

/** Value-descending copy of the summary rows. */
/** The "VS 90D" rule, shared by the Trend table and the summary tables: ✓ when
 *  the value is above its trailing 90-day average, ✗ when below, and null (→ "—")
 *  when either number is missing. */
const beats90Day = (value: number | null, avg90: number | null): boolean | null =>
  value == null || avg90 == null ? null : value > avg90;

/**
 * A "VS 90D" cell: the ✓/✗ verdict, with the numbers behind it revealed on
 * hover. The column only ever shows a symbol, so the tooltip is where the
 * underlying comparison — this period's value against its trailing 90-day
 * average — actually becomes readable.
 */
function Vs90Cell({ metric, value, avg90 }: { metric: Metric; value: number | null; avg90: number | null }) {
  const beats = beats90Day(value, avg90);
  return (
    <td className={"num " + (beats == null ? "" : beats ? "good" : "bad")} style={{ textAlign: "right" }}>
      <span className="dl-tip" tabIndex={0}>
        {beats == null ? "—" : beats ? "✓" : "✗"}
        <span className="dl-tip-box" role="tooltip">
          {avg90 == null ? (
            "No 90-day average for this period"
          ) : (
            <>
              90-day avg <b>{fmtSummaryVal(metric, avg90)}</b>
              {value != null && <> · this period <b>{fmtSummaryVal(metric, value)}</b></>}
              {beats != null && <> · {beats ? "above" : "below"} average</>}
            </>
          )}
        </span>
      </span>
    </td>
  );
}

const sortRows = (rows: MetricSummaryRow[]): MetricSummaryRow[] =>
  [...rows].sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity));

/** CSV-friendly rendering of the VS 90D verdict. */
const vs90Text = (value: number | null, avg90: number | null): string => {
  const beats = beats90Day(value, avg90);
  return beats == null ? "—" : beats ? "✓" : "✗";
};

/** The SP's numbers can arrive as strings (a `FOR JSON` document quotes some
 *  decimals), so every figure read out of one goes through this: a real number
 *  or null, never NaN and never "52.56" reaching a formatter. */
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** The SP's flags arrive as 1/0 as readily as true/false. Anything else —
 *  absent, null — stays UNKNOWN rather than defaulting to false, which would
 *  assert a direction ("lower is better") the SP never stated. */
const bool = (v: unknown): boolean | null =>
  v === true || v === 1 ? true : v === false || v === 0 ? false : null;

/** Trim + case-fold a metric code, so a code coming back from the relations SP
 *  matches the dashboard's own (and the dictionary's) even when the two differ
 *  in casing or CHAR padding. Every code lookup in this file goes through it —
 *  an unnormalized one misses silently and leaves a related metric looking
 *  unknown when it is only spelled differently. */
const normCode = (v: unknown): string => String(v ?? "").trim().toLowerCase();

/** Summary names arrive from the SP in ALL CAPS (branches, payors, referral
 *  sources, sales reps) — render them in Title Case for the drill-down. */
const titleCase = (s: string): string =>
  s.trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const StateMsg = ({ children }: { children: React.ReactNode }) => (
  <div style={{ padding: 22, textAlign: "center", color: "var(--ink-3)" }}>{children}</div>
);

/** By-Branch / By-Payor CHART view — horizontal bars, mirroring the seeded
 *  drill's `BreakdownBars`. Non-expandable. */
function SummaryBars({
  rows,
  metric,
  color,
  loading,
  error,
  emptyLabel,
  titleCaseNames = true,
  focus,
}: {
  rows: MetricSummaryRow[];
  metric: Metric;
  /** Present on a "By …" view whose filter is in the page's bar — adds a
   *  Focus button to each bar that turns the bar into the page's filter. */
  focus?: DrillFocus;
  /** Bar fill colour — the SOFT tint of the metric's trend-goodness colour
   *  (green/red/blue), so the breakdown reads as the same colour family as its
   *  KPI sparkline and trend line without the bars turning into solid blocks. */
  color: string;
  loading: boolean;
  error: string | null;
  emptyLabel: string;
  /** SP names arrive ALL CAPS and get Title-cased; mock names (clinicians,
   *  discipline codes like "PT") are already display-ready — pass false. */
  titleCaseNames?: boolean;
}) {
  const sorted = useMemo(() => sortRows(rows), [rows]);
  // Signed scale. A summary row can be negative — Revenue Adjustments is a debit
  // to revenue, so every branch/payor comes back below zero — and dividing by the
  // largest value alone gave those rows a negative bar width, i.e. no bar at all.
  // The domain therefore always includes zero, and each bar is drawn FROM the
  // zero baseline: at the left edge for the usual all-positive series, at the
  // right edge for an all-negative one, in between when the signs are mixed.
  const scale = useMemo(() => {
    const vals = sorted.map((r) => r.value ?? 0);
    const lo = Math.min(0, ...vals);
    const hi = Math.max(0, ...vals);
    const span = hi - lo || 1;
    return { lo, span, zeroPct: ((0 - lo) / span) * 100 };
  }, [sorted]);
  /** Bar geometry for one value: the segment between zero and the value. */
  const barBox = (v: number) => {
    const pct = ((v - scale.lo) / scale.span) * 100;
    const left = Math.min(pct, scale.zeroPct), width = Math.abs(pct - scale.zeroPct);
    // `min()` keeps the 2px min-width sliver of a near-zero row inside the track
    // instead of letting it hang off the right edge of an all-negative scale.
    return { left: `min(${left}%, 100% - 2px)`, width: width + "%" };
  };

  if (loading) return <StateMsg>Loading {emptyLabel} summary…</StateMsg>;
  if (error) return <StateMsg>{error}</StateMsg>;
  if (!sorted.length) return <StateMsg>No {emptyLabel} data for this metric.</StateMsg>;

  return (
    <div className="bd-bars">
      {sorted.map((r) => {
        const label = focus?.resolve(r);
        const focused = !!label && !!focus?.selected.includes(label);
        const name = titleCaseNames ? titleCase(r.name) : r.name;
        return (
        <div key={r.id} className={"bd-bar-row" + (focus ? " has-focus" : "")} style={{ cursor: "default" }}>
          {/* The label ellipsizes in its grid column — the tooltip shows it whole. */}
          <span className="bd-bar-lbl" title={name}>
            {name}
            {focused && <span className="bd-focused"> · focused</span>}
          </span>
          <span className="bd-bar-track">
            {scale.zeroPct > 0.5 && scale.zeroPct < 99.5 && (
              <span className="bd-bar-zero" style={{ left: scale.zeroPct + "%" }} />
            )}
            <span className="bd-bar-fill" style={{ ...barBox(r.value ?? 0), background: color }} />
          </span>
          <span className="bd-bar-val num">{r.value === null ? "—" : fmtSummaryVal(metric, r.value)}</span>
          {focus &&
            (!label ? (
              <span />
            ) : focused ? (
              <button type="button" className="bd-focus on" onClick={() => focus.remove(label)} title={`Remove ${label} from the filter`} aria-label={`Remove focus on ${label}`}>
                <X size={11} strokeWidth={2.5} />
              </button>
            ) : (
              <button type="button" className="bd-focus" onClick={() => focus.apply(label)} title={`Add ${label} to the filter`}>
                Focus
              </button>
            ))}
        </div>
        );
      })}
    </div>
  );
}

/** By-Branch / By-Payor TABLE view — mirrors the seeded drill's `BreakdownTable`
 *  columns (Dimension | Value | Share/vs Bench) but WITHOUT the expand/collapse
 *  affordance, per the current requirement. */
function SummaryTable({
  rows,
  metric,
  dimLabel,
  column,
  loading,
  error,
  emptyLabel,
  titleCaseNames = true,
}: {
  rows: MetricSummaryRow[];
  metric: Metric;
  dimLabel: string;
  column: SummaryColumn;
  loading: boolean;
  error: string | null;
  emptyLabel: string;
  titleCaseNames?: boolean;
}) {
  const sorted = useMemo(() => sortRows(rows), [rows]);
  const total = useMemo(() => sorted.reduce((s, r) => s + (r.value ?? 0), 0), [sorted]);

  if (loading) return <StateMsg>Loading {emptyLabel} summary…</StateMsg>;
  if (error) return <StateMsg>{error}</StateMsg>;
  if (!sorted.length) return <StateMsg>No {emptyLabel} data for this metric.</StateMsg>;

  // Third-column cell for the share variant. The vs-90-day variant renders via
  // `Vs90Cell` instead, which carries the hover tooltip.
  const shareCell = (r: MetricSummaryRow): string =>
    total ? (((r.value ?? 0) / total) * 100).toFixed(0) + "%" : "—";

  return (
    <table className="dl-tbl bd-tbl">
      <thead>
        <tr>
          <th>{dimLabel}</th>
          <th style={{ textAlign: "right" }}>Value</th>
          <th style={{ textAlign: "right" }}>{column.label}</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr key={r.id} className="bd-row">
            <td>{titleCaseNames ? titleCase(r.name) : r.name}</td>
            <td className="num" style={{ textAlign: "right" }}>{r.value === null ? "—" : fmtSummaryVal(metric, r.value)}</td>
            {column.compute === "shareOfTotal" ? (
              <td className="num" style={{ textAlign: "right" }}>{shareCell(r)}</td>
            ) : (
              <Vs90Cell metric={metric} value={r.value} avg90={r.avg90} />
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The third column of a summary Table view (after Dimension | Value). `compute`
 *  decides how the cell is filled — the share of the column total, or the
 *  value's standing against its trailing 90-day average. */
interface SummaryColumn {
  label: string;
  compute: "shareOfTotal" | "vs90Day";
}

/** Column presets — reused across views; new dashboards pick or define their own. */
const COL_VS_90D: SummaryColumn = { label: "VS 90D", compute: "vs90Day" };
const COL_SHARE: SummaryColumn = { label: "% of total", compute: "shareOfTotal" };

/** A second-level drill view. `endpoint` is the summary API to call (absent for
 *  Trend and for the not-yet-wired placeholder). `dimLabel` heads the table's
 *  first column; `column` configures its third column. `rawNames` keeps the SP's
 *  names verbatim (e.g. discipline codes like "PT") instead of Title-casing.
 *  `summarizeBy` is set when the view runs on the generic summary SP. */
interface DrillView {
  key: string;
  label: string;
  endpoint?: string;
  dimLabel?: string;
  placeholder?: boolean;
  column?: SummaryColumn;
  rawNames?: boolean;
  summarizeBy?: string;
}

/**
 * A drill TAB's third column, for the filter keys whose tab doesn't use the
 * default (`COL_SHARE`). Every view runs on the generic summary SP; this only
 * keeps the column each tab showed when it had its own per-dimension SP.
 */
const TAB_COLUMNS: Record<string, SummaryColumn> = {
  branch: COL_VS_90D,
  payor: COL_VS_90D,
  discipline: COL_VS_90D,
};

/** `filters` minus one key — a focused view's own dimension, dropped so its
 *  breakdown keeps every bar (see `summaryParams`). Null when nothing is left. */
function withoutFilter(
  filters: Record<string, string[]> | null | undefined,
  key: string,
): Record<string, string[]> | null {
  if (!filters) return null;
  const rest = Object.fromEntries(Object.entries(filters).filter(([k]) => k !== key));
  return Object.keys(rest).length ? rest : null;
}

/** A config filter on the generic summary SP, grouped by its own key
 *  (`@SummarizeBy`). Labelled by the filter's config `name`. */
function genericView(f: FilterSpec): DrillView {
  const label = filterLabel(f);
  return {
    key: f.key,
    label,
    dimLabel: label,
    endpoint: getMetricSummary,
    column: COL_SHARE,
    summarizeBy: summarizeByFor(f.key),
    rawNames: f.key === "discipline",
  };
}

/** A primary filter's drill TAB ("By …") — the generic SP grouped by the key,
 *  with the key's own third column when it has one. */
function filterView(f: FilterSpec): DrillView {
  const base = genericView(f);
  return { ...base, label: `By ${base.label}`, column: TAB_COLUMNS[f.key] ?? base.column };
}

/** The drill's tabs: Trend on every dashboard, then one per PRIMARY filter in
 *  config order. Non-primary filters go under "More ▾" instead. */
function toDrillViews(filters: FilterSpec[]): DrillView[] {
  return [{ key: "trend", label: "Trend" }, ...primaryFilters(filters).map(filterView)];
}

/**
 * One chip in the drill's scope row — the filter context the numbers below were
 * fetched under, as it reads on the dashboard's own filter bar.
 *
 * `on` marks a filter the user actually narrowed (a branch selection, a payor);
 * an untouched filter still gets a chip ("All payors") so the scope is complete,
 * just a muted one.
 */
export interface DrillScopeChip {
  /** Filter name — "Branch", "Payor". Omitted for the date range / service line,
   *  which read on their own. */
  label?: string;
  value: string;
  on?: boolean;
  /** Present only on a scope the DRILL added on top of the dashboard's filters
   *  (today: the discipline a by-Discipline card click narrowed to). Renders an
   *  × that drops that narrowing and refetches at the page's own scope, so the
   *  user can widen without closing the drill. */
  onClear?: () => void;
}

/**
 * Focus on a "By …" bar (Branch, Payor, Referral Source, Source Type, Rep,
 * Discipline, Job Title): turns the slice into the page's own filter of that
 * dimension (the dropdown), so the page re-queries with the params it already
 * sends. No chip — each is an existing filter. The page owns the filter state,
 * so it supplies all the pieces.
 */
export interface DrillFocus {
  /** The labels currently focused. Empty when the filter is on "All" (nothing
   *  or everything ticked) — no bar is marked focused then. */
  selected: string[];
  /** A summary row → the dropdown label it corresponds to. A row that matches
   *  no option resolves to its own name (added to the dropdown on Focus), or
   *  undefined when it can't be filtered on (the bar then offers no Focus). */
  resolve: (row: MetricSummaryRow) => string | undefined;
  /** Adds this label to the selection (both filters are multi-select), so
   *  several bars can be focused at once. */
  apply: (label: string) => void;
  /** Drops this label from the selection; dropping the last one → "All". */
  remove: (label: string) => void;
}

/**
 * The narrowing a drill opened from the by-Discipline card carries: every call
 * it makes is already scoped to this one discipline (via `relationBase`), so its
 * HEADLINE has to be that discipline's figure too — the dashboard metric's own
 * `currentValue` is the whole-page number and would contradict the row clicked.
 *
 * `seed` is that figure when the opener already has it (the card's own bar), so
 * the drill states the exact number the row showed, with no second request and
 * no chance of the two disagreeing. It is tied to the metric it was measured
 * for; drilling on to a RELATED metric leaves it behind, and that metric's
 * discipline figure is read from the generic summary SP, grouped by discipline, instead.
 */
export interface DrillDisciplineScope {
  discipline: string;
  seed?: { code: string; value: number | null };
}

/** The filter/date context shared with the metrics call — the Relationship API
 *  takes the same, plus the selected metric code. */
export interface RelationBase {
  serviceLineSystemID: string;
  startDate: string;
  endDate: string;
  /** The applied filters keyed by config filter `key` — the `@Filters` map every
   *  drill SP takes. */
  filters?: Record<string, string[]> | null;
}

/**
 * Drill-down Trends View for a metric selected from the KPI / More-metrics
 * sections. Chart + Table come from the Dashboard Metrics API trend series
 * already in hand; the Relationship section is fetched from the relations API.
 * Same look and overlay as the seeded DrillPanel — only the data source differs.
 */
export function MetricTrendsPanel({
  category,
  stack,
  setStack,
  onClose,
  tab,
  setTab,
  relationBase,
  scopeChips = [],
  disciplineScope = null,
  focus,
  dashboardKey = "",
  nowScope = false,
}: {
  category: DashboardMetricCategory;
  stack: string[];
  setStack: (s: string[]) => void;
  onClose: () => void;
  tab: string;
  setTab: (t: string) => void;
  relationBase: RelationBase;
  /** The dashboard's applied filters, shown in the header so the drill states
   *  the scope its numbers were fetched under. */
  scopeChips?: DrillScopeChip[];
  /** Set when the drill was opened from the by-Discipline card — see
   *  {@link DrillDisciplineScope}. Null for an ordinary KPI / More-metrics
   *  drill, which reads the dashboard metric's own value. */
  disciplineScope?: DrillDisciplineScope | null;
  /** Focus handlers for the "By …" bars, keyed by view (= filter key).
   *  Omitted (or a key missing) → that view's bars offer no Focus. */
  focus?: Partial<Record<string, DrillFocus>>;
  /** The page's dashboard key — its spec's non-primary filters are the "More ▾"
   *  breakdowns for its metrics. */
  dashboardKey?: string;
  /** Operating View only: turns on the Records link. Off for All Metrics.
   *  (The "More ▾" breakdowns show wherever the metric's dashboard config has
   *  non-primary filters; Focus is gated separately, by passing `focus`.) */
  nowScope?: boolean;
}) {
  const byCode = useMemo(() => {
    const m = new Map<string, DashboardMetric>();
    for (const metric of category.metrics) m.set(normCode(metric.code), metric);
    return m;
  }, [category]);

  const code = stack[stack.length - 1];
  /** Is the drilled metric one of THIS dashboard's? It decides where the
   *  headline figure comes from — the dashboard's own card, or the relations
   *  document's metric rows. */
  const inDashboard = byCode.has(normCode(code));

  // Everything the drill remembers is tied to the filter/date context it was
  // measured under: change the range and every remembered figure becomes a lie
  // about the new one, so the store starts empty again. Drilling DEEPER does not
  // reset it — same context, and walking back up the breadcrumb must still find
  // the rows.
  const ctxKey = JSON.stringify(relationBase);
  const detailsRef = useRef<{ ctx: string; rows: Map<string, RelatedMetricDetail> }>({
    ctx: ctxKey,
    rows: new Map(),
  });

  const relParams = code
    ? { ...relationBase, metricCodes: code }
    : null;
  const { trendValues, relations, relatedDetails, loading: relLoading } = useMetricRelations(relParams);

  // Recovery for the one case the carried rows can't cover: the drill's context
  // changed UNDER an open cross-dashboard metric — the × on the discipline scope
  // chip does exactly that — which retires every remembered figure and leaves
  // that metric with none, and no way to ask for its own (the SP details a
  // metric only to its RELATIONS). So the parent is asked again under the new
  // context and the row taken from its answer. Idle on the ordinary path: while
  // the store has the row, nothing is fetched.
  const parentCode = stack.length > 1 ? stack[stack.length - 2] : null;
  const carried = detailsRef.current;
  const needParent =
    !!code &&
    !!parentCode &&
    !inDashboard &&
    !(carried.ctx === ctxKey && carried.rows.has(normCode(code)));
  const { relatedDetails: parentDetails } = useMetricRelations(
    needParent ? { ...relationBase, metricCodes: parentCode } : null,
  );

  // A related metric often belongs to ANOTHER dashboard — Operations →
  // Admissions relates to ReferralCNT and ReferralConv, which live on Sales &
  // Funnel. `category` holds only THIS dashboard's metrics, so those resolved to
  // nothing: the row showed the bare code and the click was dead.
  //
  // `RelatedMetricDetails` answers for them — it carries each related metric's
  // value, prior value, name, unit and direction, measured under this drill's
  // own range and filters. But it describes a metric's RELATIONS, never the
  // metric that was asked for: the AdmissionCNT response details ReferralCNT,
  // and the ReferralCNT response details ReferralCNT's own relations and says
  // nothing about ReferralCNT. The figure a cross-dashboard drill renders is
  // therefore the one its PARENT step already brought back, which is why the
  // rows are accumulated across steps rather than read off the current response.
  const detailByCode = useMemo(() => {
    const store = detailsRef.current;
    if (store.ctx !== ctxKey) {
      store.ctx = ctxKey;
      store.rows = new Map();
    }
    for (const r of [...relatedDetails, ...parentDetails]) {
      const key = normCode(r.MetricCode);
      if (key) store.rows.set(key, r);
    }
    // A COPY, so the map handed downstream changes identity exactly when rows
    // land — the accumulator itself is a ref and would never look different.
    return new Map(store.rows);
  }, [ctxKey, relatedDetails, parentDetails]);

  // The metric dictionary backs the rest: it spans every dashboard of the
  // service line, so it names a related metric BEFORE the drill into it is made
  // (the list is rendered from codes) and supplies the formula line, which the
  // relations rows don't carry.
  const { data: definitions } = useMetricDefinitions(relationBase.serviceLineSystemID);
  const defByCode = useMemo(() => {
    const m = new Map<string, { def: MetricDefinition; dashboard: string }>();
    for (const d of definitions?.dashboards ?? [])
      for (const def of d.metrics) {
        const key = normCode(def.code);
        if (key && !m.has(key)) m.set(key, { def, dashboard: d.name });
      }
    return m;
  }, [definitions]);

  /** The dashboard a metric belongs to, for a metric this dashboard doesn't
   *  hold: the relations row states it outright, the dictionary is the fallback
   *  for a code the SP returned no row for. */
  const dashboardOf = useCallback(
    (c: string): string => {
      const key = normCode(c);
      return (
        detailByCode.get(key)?.MetricCategory ?? defByCode.get(key)?.dashboard ?? ""
      );
    },
    [detailByCode, defByCode],
  );

  /** The metric behind a code: this dashboard's record when it holds one,
   *  otherwise the relations row and the dictionary entry folded into the same
   *  shape. A code NEITHER source knows still resolves, to itself, so a drill is
   *  never dead — the header just names it by code and the chart carries it. */
  const resolveMetric = useCallback(
    (c: string): DashboardMetric | null => {
      const key = normCode(c);
      if (!key) return null;
      const own = byCode.get(key);
      if (own) return own;
      const row = detailByCode.get(key);
      const entry = defByCode.get(key);
      return {
        code: c,
        name: row?.MetricShortDesc || entry?.def.name || c,
        category: row?.MetricCategory ?? entry?.dashboard ?? "",
        serviceLineSystemID: relationBase.serviceLineSystemID,
        formula: row?.MetricFormula ?? entry?.def.formula ?? "",
        benchmarkLabel: row?.BenchMarkLabel ?? null,
        calc: row?.MetricCalc ?? entry?.def.calc ?? "",
        currentValue: num(row?.Curr_MetricValue),
        priorValue: num(row?.Prev_MetricValue),
        // The SP sends 1/0 as readily as true/false; anything else (absent,
        // null) leaves the direction unknown, which grades the delta neutral
        // rather than guessing which way is good.
        higherIsBetter: bool(row?.TargetHigherIsBetter),
        isKeyMetric: bool(row?.IsKeyMetric) ?? false,
        isCurrentState: bool(row?.IsCurrentState) ?? false,
        sequence: entry?.def.sequence ?? 0,
        trend: [],
        branches: [],
      };
    },
    [byCode, detailByCode, defByCode, relationBase.serviceLineSystemID],
  );

  const metric = useMemo(() => resolveMetric(code), [resolveMetric, code]);

  // Second-level view. Reset to Trend whenever the drilled metric changes
  // (mirrors the seeded drill). `activeView` also guards against a stale key
  // after a pillar change.
  const [view, setView] = useState("trend");
  useEffect(() => setView("trend"), [code]);

  // The second-level nav is the dashboard's own `filters[]`: Trend, then a
  // tab per primary filter; the non-primary ones are "More ▾" breakdowns, held
  // as the view key `more:<filter key>`.
  //
  // A drill that walked into ANOTHER dashboard's metric takes that dashboard's
  // filters instead: ReferralCNT is drilled by Referral Source / Rep (Sales &
  // Funnel's config), not by whatever the page it was opened from offers. A
  // dashboard with no spec (not granted to this role) gets the default filters.
  const { specFor } = useStore();
  const ownerSpec = specFor(inDashboard ? dashboardKey : pillarKey(dashboardOf(code)));
  const ownerFilters = ownerSpec?.filters ?? DEFAULT_SPEC.filters;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const views = useMemo(() => toDrillViews(ownerFilters), [JSON.stringify(ownerFilters)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const moreDims = useMemo(() => secondaryFilters(ownerFilters).map(genericView), [JSON.stringify(ownerFilters)]);
  // The Records link names the grain of the metric's dashboard — the config's
  // `drillGrain` ("Drill by Referral source · Rep"), minus the prefix the main
  // screen's chip carries.
  const recordsGrain =
    (ownerSpec?.drillGrain ?? "").replace(/^drill\s+by\s+/i, "").trim() || "patient level";
  const moreDim = view.startsWith("more:") ? moreDims.find((d) => d.key === view.slice(5)) : undefined;
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!moreOpen) return;
    const close = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [moreOpen]);

  const activeView: DrillView = moreDim
    ? { ...moreDim, key: view }
    : (views.find((v) => v.key === view) ?? views[0]);
  const effView = activeView.key;
  // The filter the open view breaks down by: its own key on a "By …" tab, the
  // filter's key (not the `more:` view key) on a More ▾ breakdown.
  const viewDim = moreDim ? moreDim.key : effView;
  // A More ▾ breakdown offers Focus too when the page has that filter as a
  // "+ Focus" dimension — Focus then adds a chip instead of setting a dropdown.
  const viewFocus = focus?.[viewDim];
  // The filter the Focus hint under Records names. None on Trend, or on a
  // breakdown the page can't filter by.
  const focusHintDim = viewFocus ? (activeView.dimLabel ?? null) : null;

  // One summary fetch, LAZILY driven by the active view's endpoint — so only the
  // open view's API is called (Trend/placeholder call nothing), and it serves
  // both the Chart and Table sub-views. Refetches when the endpoint (view),
  // metric, range, or any filter changes.
  //
  // A Focus-able view is fetched WITHOUT its own dimension's filter, so every
  // bar stays on screen after a Focus (the focused one is marked) instead of
  // the breakdown collapsing to the single bar just picked. Every other filter
  // still applies.
  const summaryParams = code
    ? {
        ...relationBase,
        metricCodes: code,
        ...(viewFocus ? { filters: withoutFilter(relationBase.filters, viewDim) } : {}),
        ...(activeView.summarizeBy ? { summarizeBy: activeView.summarizeBy } : {}),
      }
    : null;
  const summaryActive = !!activeView.endpoint && !activeView.placeholder && !!summaryParams;
  const summary = useMetricSummary(activeView.endpoint ?? "", summaryActive ? summaryParams : null);

  // ── The headline figure under a discipline scope ───────────────────────────
  // `metric.currentValue` is the DASHBOARD's number: the whole page's scope, all
  // disciplines. A drill opened from the by-Discipline card is narrowed to one
  // of them, so that value would restate the card's total next to a "Discipline:
  // PT" chip — the mismatch this exists to fix.
  //
  // The clicked row's own figure is preferred (`seed`): no request, and the
  // number is character-for-character the one the bar showed. It only covers the
  // metric the card plots, so a drill that walked on to a RELATED metric falls
  // back to the generic summary SP grouped by discipline, fetched under `relationBase` — which
  // already carries the single discipline, so it comes back as that one row.
  const seed =
    disciplineScope?.seed &&
    disciplineScope.seed.code.trim().toLowerCase() === code.trim().toLowerCase()
      ? disciplineScope.seed
      : null;
  const scopedParams =
    disciplineScope && !seed && code
      ? { ...relationBase, metricCodes: code, summarizeBy: summarizeByFor("discipline") }
      : null;
  const scopedSummary = useMetricSummary(getMetricSummary, scopedParams);
  const scopedRow = scopedParams
    ? (scopedSummary.rows.find(
        (r) =>
          r.name.trim().toLowerCase() ===
          disciplineScope!.discipline.trim().toLowerCase(),
      ) ?? null)
    : null;

  const adapted = useMemo(() => (metric ? apiToMetric(metric) : null), [metric]);

  // The rows the active breakdown renders and exports.
  const activeRows = summary.rows;
  const activeLoading = summary.loading;
  const activeError = summary.error;

  // Chart/table series: the trend's prior periods + the current period appended.
  // Trend series comes from the relations API's `MetricTrendValues` (for the
  // selected metric code), not the dashboard-metric response — it already spans
  // all periods including the current one, and works for any metric code.
  const trendRows = useMemo(() => {
    const wanted = code.trim().toLowerCase();
    return trendValues
      .filter(
        (t) =>
          t.MetricValue !== null &&
          t.MetricValue !== undefined &&
          !!t.SummaryPeriodStartDate &&
          (!t.MetricCode || t.MetricCode.trim().toLowerCase() === wanted),
      )
      .slice()
      .sort((a, b) => (a.SummaryPeriodStartDate ?? "").localeCompare(b.SummaryPeriodStartDate ?? ""));
  }, [trendValues, code]);
  // The SP buckets by whatever interval the RANGE implies and doesn't say which,
  // so the period labels read the grain off the spacing of what came back — a
  // twelve-week or twelve-day window (the All Metrics Week/Day drills) would
  // otherwise repeat one "Aug '26" across every point.
  const trendGrain = useMemo(
    () => seriesGranularity(trendRows.map((t) => String(t.SummaryPeriodStartDate ?? ""))),
    [trendRows],
  );
  const trendPoints = useMemo(
    () =>
      trendRows.map((t) => ({
        iso: t.SummaryPeriodStartDate as string,
        label: periodLabelWithYear(t.SummaryPeriodStartDate as string, trendGrain),
        value: Number(t.MetricValue),
        avg90: t.MetricValue90DayAvg != null ? Number(t.MetricValue90DayAvg) : null,
      })),
    [trendRows, trendGrain],
  );
  // The chart gets the SHORT label ("Jul 20"); `p.label` keeps its year for the
  // Table tab and the CSV, which are read without the date-range chip in view.
  const buckets = useMemo<Bucket[]>(
    () => trendPoints.map((p) => ({ label: periodAxisLabel(p.iso, trendGrain), value: p.value })),
    [trendPoints, trendGrain],
  );

  // Related metrics for the Relationship section. Every row drills — a related
  // metric belonging to another dashboard is still this metric's driver, and
  // reading it meant leaving the drill, switching tabs and finding it by hand.
  // `dashboard` is set only for those, so the row says where the click leads.
  const related = useMemo(() => {
    const seen = new Set<string>();
    const out: { code: string; name: string; dashboard: string | null }[] = [];
    const here = normCode(code);
    for (const r of relations) {
      if (here && r.MetricCode && normCode(r.MetricCode) !== here) continue;
      const rc = r.RelatedMetricCode;
      const key = normCode(rc);
      if (!rc || !key || seen.has(key)) continue;
      seen.add(key);
      const own = byCode.get(key);
      const row = detailByCode.get(key);
      const entry = defByCode.get(key);
      out.push({
        code: rc,
        name: own?.name ?? row?.MetricShortDesc ?? entry?.def.name ?? rc,
        dashboard: own ? null : (dashboardOf(rc) || null),
      });
    }
    return out;
  }, [relations, code, byCode, detailByCode, defByCode, dashboardOf]);

  // Export either the trend series or the active summary table (when available).
  const exportCurrent = () => {
    if (!metric || !adapted) return;
    if (effView === "trend") {
      // Export the period WITH its year ("May '25") — the on-screen axis can
      // drop it for space, but a CSV is read out of context and can span a year
      // end.
      const rows: unknown[][] = [["Period", metric.name, "90D Avg", "VS 90D"]];
      trendPoints.forEach((p) =>
        rows.push([
          periodLabelWithYear(p.iso, trendGrain),
          p.value,
          p.avg90 ?? "—",
          vs90Text(p.value, p.avg90),
        ]),
      );
      downloadCsv(`DayLight_Trend_${metric.code}.csv`, rows);
      return;
    }

    if (activeView.placeholder || (!activeView.endpoint && !moreDim)) return;
    const sorted = sortRows(activeRows);
    const column = activeView.column ?? COL_SHARE;
    const dimLabel = activeView.dimLabel ?? "Dimension";
    const total = sorted.reduce((s, r) => s + (r.value ?? 0), 0);
    const shareText = (r: MetricSummaryRow): string =>
      total ? (((r.value ?? 0) / total) * 100).toFixed(0) + "%" : "—";

    const rows: unknown[][] = [[dimLabel, "Value", "90D Avg", column.label]];
    sorted.forEach((r) => {
      const c3 =
        column.compute === "shareOfTotal"
          ? shareText(r)
          : vs90Text(r.value, r.avg90);
      rows.push([
        activeView.rawNames ? r.name : titleCase(r.name),
        r.value,
        r.avg90 ?? "—",
        c3,
      ]);
    });
    downloadCsv(`DayLight_Summary_${metric.code}_${activeView.key}.csv`, rows);
  };

  if (!metric || !adapted) return null;

  // Scoped → the discipline's own figure, and NO prior period to compare it
  // against (neither source carries one per discipline), so the delta reads "—"
  // rather than repeating the whole-dashboard change beside a one-discipline
  // number.
  //
  // Unscoped, one of this dashboard's own metrics reads its card exactly as
  // before; a metric from another dashboard has no card here, so it reads the
  // `RelatedMetricDetails` row its parent step brought back — the same
  // period-over-period pair for the same range and filters. With no such row it
  // reads "—": the trend series is NOT a stand-in for it, since it ends at the
  // period BEFORE the range (a July figure under an August header would be
  // wrong, not approximate).
  const value = disciplineScope
    ? seed
      ? seed.value
      : (scopedRow?.value ?? null)
    : metric.currentValue;
  const priorValue = disciplineScope ? null : metric.priorValue;
  const delta = deltaOf(value, priorValue);
  const dg = value === null || priorValue === null ? "neutral" : deltaGoodness(adapted, delta.pct);
  // Colour the trend line the same way the KPI sparkline is coloured — by trend
  // goodness, since there is no numeric benchmark to grade against.
  const trendColor = dg === "good" ? "var(--green)" : dg === "bad" ? "var(--red)" : "var(--chart-primary)";
  // The summary tabs (By Branch / Payor / Referral Source / …) fill whole bars
  // rather than draw a line, so they take the soft tint of the same colour — at
  // full strength a column of solid bars reads far heavier than the trend line.
  const summaryColor =
    dg === "good" ? "var(--green-soft)" : dg === "bad" ? "var(--red-soft)" : "var(--chart-primary-soft)";

  const openRelated = (rc: string) => setStack([...stack, rc]);

  return (
    <>
      <div className="dl-scrim" onClick={onClose} />
      <div className="dl-drill dl">
        <div className="dl-drillhead">
          <div className="dl-bread">
            {stack.map((sid, i) => (
              <Fragment key={sid + i}>
                {i > 0 && <ChevronRight size={11} />}
                {i < stack.length - 1 ? (
                  <button onClick={() => setStack(stack.slice(0, i + 1))}>{resolveMetric(sid)?.name ?? sid}</button>
                ) : (
                  <span style={{ color: "var(--ink-1)", fontWeight: 600 }}>{resolveMetric(sid)?.name ?? sid}</span>
                )}
              </Fragment>
            ))}
            <div style={{ flex: 1 }} />
            <button onClick={onClose} style={{ color: "var(--ink-3)" }}><X size={16} /></button>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
            <div className="num" style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-.02em" }}>
              {value === null
                ? scopedSummary.loading || relLoading
                  ? "…"
                  : "—"
                : fmtVal(adapted, value)}
            </div>
            <span className={"dl-delta num " + dg} style={{ fontSize: 14 }}>
              {dg === "good" ? <ArrowUpRight size={15} /> : dg === "bad" ? <ArrowDownRight size={15} /> : <Minus size={13} />}
              {priorValue === null ? "—" : formatAbsDelta(adapted, delta)}
            </span>
            {SHOW_BENCHMARK_LABEL && metric.benchmarkLabel && <span className="dl-bench bench-neutral">{metric.benchmarkLabel}</span>}
          </div>
          {/* The dashboard's filter bar, restated: what this drill was fetched
              under. Narrowed filters read in the accent, untouched ones ("All
              payors") stay muted so the scope is complete without shouting. */}
          {scopeChips.length > 0 && (
            <div className="dl-scope">
              {scopeChips.map((c) => (
                <span key={(c.label ?? "") + c.value} className={"dl-scope-chip" + (c.on ? " on" : "")}>
                  {c.label && <span className="dl-scope-lbl">{c.label}:</span>}
                  {c.value}
                  {c.onClear && (
                    <button
                      type="button"
                      className="dl-scope-x"
                      onClick={c.onClear}
                      title={`Clear ${c.label ?? "this"} scope`}
                      aria-label={`Clear ${c.label ?? "this"} scope`}
                    >
                      <X size={10} />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}
          {metric.formula && (
            <div className="dl-formula"><strong style={{ color: "var(--ink-1)" }}>Formula · </strong>{metric.formula}</div>
          )}
          <div className="dl-drilltabs">
            <button className={tab === "chart" ? "on" : ""} onClick={() => setTab("chart")}><BarChart3 size={13} />Chart</button>
            <button className={tab === "table" ? "on" : ""} onClick={() => setTab("table")}><Table2 size={13} />Table</button>
            <button className={tab === "compare" ? "on" : ""} onClick={() => setTab("compare")}><GitBranch size={13} />Compare</button>
          </div>
        </div>
        <div className="dl-drillbody">
          {tab === "compare" ? (
            <DrillCompareView key={code} subject={adapted} subjectBuckets={buckets} metrics={category.metrics} relationBase={relationBase} onExit={() => setTab("chart")} />
          ) : (
            <>
              {/* Second-level navigation — pillar-dependent. */}
              <div className="bd-viewrow">
                <div className="bd-switch">
                  <span className="bd-switch-lbl">View</span>
                  {views.map((v) => (
                    <button key={v.key} className={effView === v.key ? "on" : ""} onClick={() => setView(v.key)}>{v.label}</button>
                  ))}
                </div>
                {moreDims.length > 0 && (
                <div className="bd-more" ref={moreRef}>
                  <button type="button" className={"bd-more-btn" + (moreDim ? " on" : "")} onClick={() => setMoreOpen((o) => !o)}>
                    More{moreDim ? ` · ${moreDim.label}` : ""} <ChevronDown size={13} />
                  </button>
                  {moreOpen && (
                    <div className="bd-more-pop" role="menu">
                      <div className="bd-more-ph">Breakdowns for this metric</div>
                      {moreDims.map((dim) => (
                        <button
                          key={dim.key}
                          type="button"
                          role="menuitem"
                          className={"bd-more-item" + (moreDim?.key === dim.key ? " on" : "")}
                          onClick={() => {
                            setView(`more:${dim.key}`);
                            setMoreOpen(false);
                          }}
                        >
                          {dim.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>

              {effView === "trend" ? (
                relLoading && buckets.length === 0 ? (
                  <StateMsg>Loading trend…</StateMsg>
                ) : buckets.length === 0 ? (
                  <StateMsg>No trend data for this metric.</StateMsg>
                ) : tab === "chart" ? (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".04em" }}>Trend</div>
                      <button className="dl-btn" onClick={exportCurrent}><Download size={13} />Export</button>
                    </div>
                    <TrendChartView metric={adapted} buckets={buckets} gran={trendGrain} color={trendColor} />
                  </>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                      <button className="dl-btn" onClick={exportCurrent}><Download size={13} />Export</button>
                    </div>
                    <table className="dl-tbl">
                    <thead><tr><th>Period</th><th style={{ textAlign: "right" }}>Value</th><th style={{ textAlign: "right" }}>VS 90D</th></tr></thead>
                    <tbody>
                      {trendPoints.map((p, i) => (
                        // ✓ (green) when the value is above its trailing 90-day
                        // average, ✗ (red) when below; "—" when no average. The
                        // numbers behind the symbol show on hover.
                        <tr key={i}>
                          <td>{p.label}</td>
                          <td className="num" style={{ textAlign: "right" }}>{fmtVal(adapted, p.value)}</td>
                          <Vs90Cell metric={adapted} value={p.value} avg90={p.avg90} />
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </>
                )
              ) : activeView.placeholder ? (
                <StateMsg>{activeView.label} is coming soon.</StateMsg>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div className="bd-cap" style={{ margin: 0 }}>{metric.name} by {activeView.dimLabel}</div>
                    <button className="dl-btn" onClick={exportCurrent}><Download size={13} />Export</button>
                  </div>
                  {tab === "chart" ? (
                    <SummaryBars rows={activeRows} metric={adapted} color={summaryColor} loading={activeLoading} error={activeError} emptyLabel={(activeView.dimLabel ?? "").toLowerCase()} titleCaseNames={!activeView.rawNames} focus={viewFocus} />
                  ) : (
                    <SummaryTable rows={activeRows} metric={adapted} dimLabel={activeView.dimLabel ?? ""} column={activeView.column ?? COL_SHARE} loading={activeLoading} error={activeError} emptyLabel={(activeView.dimLabel ?? "").toLowerCase()} titleCaseNames={!activeView.rawNames} />
                  )}
                </>
              )}
              {/* Records at the dashboard's drill grain — a label only for now. */}
              {nowScope && (
                <div className="bd-recs">
                  Records › <span>{recordsGrain}</span>
                </div>
              )}
              {/* What a Focus on this view does — under the Records link, as
                  the design has it. */}
              {focusHintDim && moreDim ? (
                <div className="bd-hint">
                  <b>{focusHintDim}</b> is a Focus dimension: Focus adds a chip to the filter bar (multi-select); × removes. Same chip "+ Focus" makes.
                </div>
              ) : focusHintDim ? (
                <div className="bd-hint">
                  <b>{focusHintDim}</b> is an existing filter: Focus adds to the {focusHintDim} dropdown (multi-select); × removes. No chip.
                </div>
              ) : moreDim ? (
                <div className="bd-hint">
                  <b>{moreDim.label}</b> breakdown is read-only — a GROUP BY on the drill query.
                </div>
              ) : null}
            </>
          )}

          {/* Related metrics show under Chart/Table (any view: Trend/Branch/
              Payor/Rep) but NOT under Compare. Clicking one drills into that
              metric, which resets to its Trend view. */}
          {tab !== "compare" && related.length > 0 && (
            <div className="dl-drivers">
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 11 }}>Related metrics</div>
              {related.map((r) => (
                <div
                  key={r.code}
                  className="dl-driver click"
                  onClick={() => openRelated(r.code)}
                >
                  <div className="dl-drow">
                    <span className="dl-dlvl">REL</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{r.name}</div>
                      <div className="dl-drel">
                        {r.dashboard ? `${r.dashboard} · ` : ""}Related to {metric.name}
                      </div>
                    </div>
                  </div>
                  <ChevronRight size={15} style={{ color: "var(--hermes)" }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
